import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, Select, Button, Input, Space, Divider, Popconfirm, Tag } from 'antd';
import {
    DeleteOutlined,
    CopyOutlined,
    LinkOutlined,
    ColumnWidthOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

// State options for organisms
const COMPONENT_STATES = ['default', 'loading', 'empty', 'error'];

// Debounce delay in ms
const DEBOUNCE_DELAY = 500;

/**
 * Custom hook for debounced value updates
 */
const useDebouncedUpdate = (value, onUpdate, delay = DEBOUNCE_DELAY) => {
    const [localValue, setLocalValue] = useState(value);
    const timeoutRef = useRef(null);
    const isFirstRender = useRef(true);

    // Sync local value when external value changes
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Debounced update
    useEffect(() => {
        // Skip first render
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
            if (localValue !== value) {
                onUpdate(localValue);
            }
        }, delay);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [localValue, value, onUpdate, delay]);

    return [localValue, setLocalValue];
};

/**
 * Component Inspector - Shows properties of selected component
 * Uses debounced updates to prevent lag when typing
 */
const ComponentInspector = ({
    component,
    artboards,
    onStateChange,
    onDelete,
    onUpdate
}) => {
    // Debounced local state for text inputs
    const handlePositionXUpdate = useCallback((val) => {
        onUpdate(component.id, { position_x: parseInt(val, 10) || 0 });
    }, [component?.id, onUpdate]);

    const handlePositionYUpdate = useCallback((val) => {
        onUpdate(component.id, { position_y: parseInt(val, 10) || 0 });
    }, [component?.id, onUpdate]);

    const handleWidthUpdate = useCallback((val) => {
        onUpdate(component.id, { width: parseInt(val, 10) || 0 });
    }, [component?.id, onUpdate]);

    const handleHeightUpdate = useCallback((val) => {
        onUpdate(component.id, { height: parseInt(val, 10) || 0 });
    }, [component?.id, onUpdate]);

    const [localPosX, setLocalPosX] = useDebouncedUpdate(
        Math.round(component?.position_x || 0),
        handlePositionXUpdate
    );
    const [localPosY, setLocalPosY] = useDebouncedUpdate(
        Math.round(component?.position_y || 0),
        handlePositionYUpdate
    );
    const [localWidth, setLocalWidth] = useDebouncedUpdate(
        Math.round(component?.width || 0),
        handleWidthUpdate
    );
    const [localHeight, setLocalHeight] = useDebouncedUpdate(
        Math.round(component?.height || 0),
        handleHeightUpdate
    );

    // Content overrides
    const handleLabelUpdate = useCallback((val) => {
        onUpdate(component.id, {
            content_overrides: { ...component.content_overrides, label: val }
        });
    }, [component?.id, component?.content_overrides, onUpdate]);

    const handleTitleUpdate = useCallback((val) => {
        onUpdate(component.id, {
            content_overrides: { ...component.content_overrides, title: val }
        });
    }, [component?.id, component?.content_overrides, onUpdate]);

    const handleDescriptionUpdate = useCallback((val) => {
        onUpdate(component.id, {
            content_overrides: { ...component.content_overrides, description: val }
        });
    }, [component?.id, component?.content_overrides, onUpdate]);

    const handlePlaceholderUpdate = useCallback((val) => {
        onUpdate(component.id, {
            content_overrides: { ...component.content_overrides, placeholder: val }
        });
    }, [component?.id, component?.content_overrides, onUpdate]);

    const [localLabel, setLocalLabel] = useDebouncedUpdate(
        component?.content_overrides?.label || 'Button',
        handleLabelUpdate
    );
    const [localTitle, setLocalTitle] = useDebouncedUpdate(
        component?.content_overrides?.title || '',
        handleTitleUpdate
    );
    const [localDescription, setLocalDescription] = useDebouncedUpdate(
        component?.content_overrides?.description || '',
        handleDescriptionUpdate
    );
    const [localPlaceholder, setLocalPlaceholder] = useDebouncedUpdate(
        component?.content_overrides?.placeholder || 'Enter text...',
        handlePlaceholderUpdate
    );

    if (!component) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Text style={{ color: '#8b949e' }}>
                    Select a component to inspect
                </Text>
            </div>
        );
    }

    // Find which artboard this component is on
    const artboard = artboards.find(a => a.id === component.artboard_id);

    // Check if this is an organism (has states)
    const hasStates = ['mobileNav', 'desktopNav', 'listItem', 'card'].includes(component.component_type);

    // Handle spacing override change (non-debounced, less frequent)
    const handleSpacingChange = (type, direction, value) => {
        const numValue = parseInt(value, 10) || 0;
        onUpdate(component.id, {
            spacing_overrides: {
                ...component.spacing_overrides,
                [type]: {
                    ...component.spacing_overrides?.[type],
                    [direction]: numValue
                }
            }
        });
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{
                padding: '16px',
                borderBottom: '1px solid #30363d'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Title level={5} style={{ margin: 0, color: '#c9d1d9' }}>
                        Inspector
                    </Title>
                    <Popconfirm
                        title="Delete this component?"
                        onConfirm={() => onDelete(component.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </div>

                {/* Component type */}
                <Tag style={{
                    background: '#238636',
                    border: 'none',
                    color: '#fff',
                    marginBottom: 8
                }}>
                    {component.component_type}
                </Tag>

                {/* Artboard info */}
                <Text style={{ color: '#8b949e', fontSize: 12, display: 'block' }}>
                    On: {artboard?.name || artboard?.device_type || 'Unknown'}
                </Text>

                {/* Synced instances */}
                {component.synced_instances?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        <Space size={4}>
                            <LinkOutlined style={{ color: '#58a6ff', fontSize: 12 }} />
                            <Text style={{ color: '#58a6ff', fontSize: 11 }}>
                                Synced to {component.synced_instances.length} other artboard(s)
                            </Text>
                        </Space>
                    </div>
                )}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                {/* State Selector (for organisms) */}
                {hasStates && (
                    <>
                        <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                            STATE
                        </Text>
                        <Select
                            value={component.state || 'default'}
                            onChange={(value) => onStateChange(component.id, value)}
                            style={{ width: '100%', marginBottom: 16 }}
                        >
                            {COMPONENT_STATES.map(state => (
                                <Option key={state} value={state}>
                                    {state.charAt(0).toUpperCase() + state.slice(1)}
                                </Option>
                            ))}
                        </Select>
                        <Divider style={{ borderColor: '#30363d', margin: '16px 0' }} />
                    </>
                )}

                {/* Position & Size */}
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                    POSITION & SIZE
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                    <div>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>X</Text>
                        <Input
                            size="small"
                            value={localPosX}
                            onChange={(e) => setLocalPosX(e.target.value)}
                            style={{ background: '#0d1117', border: '1px solid #30363d' }}
                        />
                    </div>
                    <div>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Y</Text>
                        <Input
                            size="small"
                            value={localPosY}
                            onChange={(e) => setLocalPosY(e.target.value)}
                            style={{ background: '#0d1117', border: '1px solid #30363d' }}
                        />
                    </div>
                    <div>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Width</Text>
                        <Input
                            size="small"
                            value={localWidth}
                            onChange={(e) => setLocalWidth(e.target.value)}
                            style={{ background: '#0d1117', border: '1px solid #30363d' }}
                        />
                    </div>
                    <div>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Height</Text>
                        <Input
                            size="small"
                            value={localHeight}
                            onChange={(e) => setLocalHeight(e.target.value)}
                            style={{ background: '#0d1117', border: '1px solid #30363d' }}
                        />
                    </div>
                </div>

                <Divider style={{ borderColor: '#30363d', margin: '16px 0' }} />

                {/* Content Overrides */}
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                    CONTENT
                </Text>

                {component.component_type === 'button' && (
                    <div style={{ marginBottom: 8 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Label</Text>
                        <Input
                            size="small"
                            value={localLabel}
                            onChange={(e) => setLocalLabel(e.target.value)}
                            style={{ background: '#0d1117', border: '1px solid #30363d' }}
                        />
                    </div>
                )}

                {component.component_type === 'input' && (
                    <div style={{ marginBottom: 8 }}>
                        <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Placeholder</Text>
                        <Input
                            size="small"
                            value={localPlaceholder}
                            onChange={(e) => setLocalPlaceholder(e.target.value)}
                            style={{ background: '#0d1117', border: '1px solid #30363d' }}
                        />
                    </div>
                )}

                {(component.component_type === 'card' || component.component_type === 'listItem') && (
                    <>
                        <div style={{ marginBottom: 8 }}>
                            <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Title</Text>
                            <Input
                                size="small"
                                value={localTitle}
                                onChange={(e) => setLocalTitle(e.target.value)}
                                style={{ background: '#0d1117', border: '1px solid #30363d' }}
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Description</Text>
                            <Input.TextArea
                                size="small"
                                rows={2}
                                value={localDescription}
                                onChange={(e) => setLocalDescription(e.target.value)}
                                style={{ background: '#0d1117', border: '1px solid #30363d' }}
                            />
                        </div>
                    </>
                )}

                <Divider style={{ borderColor: '#30363d', margin: '16px 0' }} />

                {/* Spacing */}
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                    <ColumnWidthOutlined style={{ marginRight: 4 }} />
                    SPACING (MARGIN)
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4, marginBottom: 12 }}>
                    {['top', 'right', 'bottom', 'left'].map(dir => (
                        <div key={dir}>
                            <Text style={{ color: '#484f58', fontSize: 9, display: 'block', marginBottom: 2 }}>
                                {dir.charAt(0).toUpperCase()}
                            </Text>
                            <Input
                                size="small"
                                value={component.spacing_overrides?.margin?.[dir] || 0}
                                onBlur={(e) => handleSpacingChange('margin', dir, e.target.value)}
                                onPressEnter={(e) => handleSpacingChange('margin', dir, e.target.value)}
                                style={{ background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                            />
                        </div>
                    ))}
                </div>

                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                    <ColumnWidthOutlined style={{ marginRight: 4 }} />
                    PADDING
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4 }}>
                    {['top', 'right', 'bottom', 'left'].map(dir => (
                        <div key={dir}>
                            <Text style={{ color: '#484f58', fontSize: 9, display: 'block', marginBottom: 2 }}>
                                {dir.charAt(0).toUpperCase()}
                            </Text>
                            <Input
                                size="small"
                                value={component.spacing_overrides?.padding?.[dir] || 0}
                                onBlur={(e) => handleSpacingChange('padding', dir, e.target.value)}
                                onPressEnter={(e) => handleSpacingChange('padding', dir, e.target.value)}
                                style={{ background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer Actions */}
            <div style={{
                padding: 16,
                borderTop: '1px solid #30363d',
                display: 'flex',
                gap: 8
            }}>
                <Button
                    icon={<CopyOutlined />}
                    style={{ flex: 1 }}
                    disabled
                >
                    Duplicate
                </Button>
                <Button
                    icon={<LinkOutlined />}
                    style={{ flex: 1 }}
                    disabled
                >
                    Sync
                </Button>
            </div>
        </div>
    );
};

export default ComponentInspector;
